/**
 * Activity — v2 audit trail.
 *
 * TWO EXPERIENCES gated by `user.owner`:
 *
 *   • **App Owner** → boardroom-grade audit console fetched from
 *     /api/audit/* (envelope, hash chain, session drawer, live-tail,
 *     CSV / PDF export). Owners see everything.
 *
 *   • **Admin (non-Owner)** → simplified file history table fetched
 *     from /api/audit/file-history. Column shape: File · Action ·
 *     Actor · Room · When. Purpose: give admins "who did what with
 *     which file" for day-to-day room management without exposing
 *     forensics data (IP / geo / hash chain / session id).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Activity as ActivityIcon, RefreshCw, Search, ShieldCheck,
  ShieldAlert, Download, FileDown, X, ChevronDown, ChevronUp,
  Upload, Trash2, Pencil, Move, Copy, MessagesSquare, ListChecks,
  Users, LogIn, LogOut, Eye, FileText, Folder, Play,
  Globe, Radio, Loader2,
} from "lucide-react";

const ACTION_META = {
  document_uploaded:            { icon: Upload,         label: "Uploaded" },
  document_downloaded_pdf:      { icon: FileDown,       label: "Downloaded PDF" },
  document_downloaded_original: { icon: FileDown,       label: "Downloaded original" },
  document_downloaded_encrypted:{ icon: FileDown,       label: "Downloaded encrypted PDF" },
  document_viewed:              { icon: Eye,            label: "Viewed" },
  document_renamed:             { icon: Pencil,         label: "Renamed file" },
  document_moved:               { icon: Move,           label: "Moved file" },
  document_copied:              { icon: Copy,           label: "Copied file" },
  document_deleted:             { icon: Trash2,         label: "Deleted file" },
  document_access_updated:      { icon: ShieldCheck,    label: "Updated document access" },
  folder_created:               { icon: Folder,         label: "Created folder" },
  folder_renamed:               { icon: Pencil,         label: "Renamed folder" },
  folder_moved:                 { icon: Move,           label: "Moved folder" },
  folder_copied:                { icon: Copy,           label: "Copied folder" },
  folder_deleted:               { icon: Trash2,         label: "Deleted folder" },
  room_created:                 { icon: Folder,         label: "Created room" },
  room_deleted:                 { icon: Trash2,         label: "Deleted room" },
  member_invited:               { icon: Users,          label: "Invited member" },
  member_removed:               { icon: Users,          label: "Removed member" },
  login:                        { icon: LogIn,          label: "Signed in" },
  login_mfa:                    { icon: LogIn,          label: "Signed in (MFA)" },
  logout:                       { icon: LogOut,         label: "Signed out" },
  self_account_deleted:         { icon: Trash2,         label: "Deleted own account" },
  question_asked:               { icon: MessagesSquare, label: "Asked a question" },
  question_answered:            { icon: MessagesSquare, label: "Answered a question" },
  checklist_seeded:             { icon: ListChecks,     label: "Seeded checklist" },
  checklist_item_updated:       { icon: ListChecks,     label: "Updated checklist item" },
  page_view:                    { icon: Eye,            label: "Viewed page" },
  task_created:                 { icon: ListChecks,     label: "Created task" },
  task_updated:                 { icon: ListChecks,     label: "Updated task" },
  task_deleted:                 { icon: Trash2,         label: "Deleted task" },
  owner_invite_sent:            { icon: Users,          label: "Sent invite" },
};

const SEVERITY_TONE = {
  INFO:        { dot: "bg-neutral-400", text: "text-neutral-500", chip: "bg-neutral-500/10 border-neutral-500/30 text-neutral-500", soft: "bg-neutral-500/10" },
  DATA_ACCESS: { dot: "bg-sky-500",     text: "text-sky-500",     chip: "bg-sky-500/10 border-sky-500/30 text-sky-500",             soft: "bg-sky-500/10" },
  SECURITY:    { dot: "bg-amber-500",   text: "text-amber-500",   chip: "bg-amber-500/10 border-amber-500/30 text-amber-500",       soft: "bg-amber-500/10" },
  DESTRUCTIVE: { dot: "bg-rose-500",    text: "text-rose-500",    chip: "bg-rose-500/10 border-rose-500/30 text-rose-500",          soft: "bg-rose-500/10" },
};

const CATEGORIES = [
  { key: "auth",       label: "Auth" },
  { key: "documents",  label: "Docs & folders" },
  { key: "members",    label: "Members" },
  { key: "rooms",      label: "Rooms" },
  { key: "qa",         label: "Q&A" },
  { key: "tasks",      label: "Tasks" },
  { key: "checklist",  label: "Checklist" },
  { key: "system",     label: "System" },
];

const DATE_SHORTCUTS = [
  { key: 1,   label: "24h" },
  { key: 7,   label: "7d" },
  { key: 30,  label: "30d" },
  { key: 90,  label: "90d" },
  { key: 365, label: "1y" },
];

function fmtRelative(iso) {
  const ts = new Date(iso).getTime();
  const diff = (Date.now() - ts) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}
function fmtAbsolute(iso) {
  return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "medium" });
}
function humanDateHeader(d) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const yest = new Date(today); yest.setDate(today.getDate() - 1);
  const dd = new Date(d); dd.setHours(0, 0, 0, 0);
  if (dd.getTime() === today.getTime()) return "Today";
  if (dd.getTime() === yest.getTime()) return "Yesterday";
  return d.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric", year: "numeric" });
}
function fmtDelta(n) {
  if (n === 0) return "no change";
  if (n > 0) return `+${n}`;
  return `${n}`;
}

function KpiCard({ label, value, delta, Icon, tone }) {
  const positive = (delta ?? 0) >= 0;
  return (
    <div className="border border-border rounded-lg p-4 bg-card">
      <div className="flex items-center justify-between">
        <span className="label-mono text-muted-foreground">{label}</span>
        <span className={`w-7 h-7 grid place-items-center rounded-md ${tone}`}>
          <Icon className="w-3.5 h-3.5" />
        </span>
      </div>
      <div className="mt-2 font-display text-3xl tracking-tight">{value.toLocaleString()}</div>
      <div className={`mt-1 mono text-[10px] tracking-wider uppercase ${positive ? "text-emerald-500" : "text-rose-500"}`}>
        {fmtDelta(delta ?? 0)} vs prior
      </div>
    </div>
  );
}

function ChainPill({ checked, intact, onOpen }) {
  const cls = intact
    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-500"
    : "border-rose-500/40 bg-rose-500/10 text-rose-500";
  const Icon = intact ? ShieldCheck : ShieldAlert;
  return (
    <button
      onClick={onOpen}
      data-testid="chain-pill"
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border ${cls} mono text-[10px] uppercase tracking-wider hover:opacity-90 transition`}
      title="SHA-256 hash-chain integrity check. Click to re-run."
    >
      <Icon className="w-3.5 h-3.5" />
      {intact
        ? `Log verified · ${checked.toLocaleString()} events · SHA-256 chain intact`
        : `Chain break detected — click to investigate`}
    </button>
  );
}

function EventRow({ item, onSession, expanded, onToggle }) {
  const meta = ACTION_META[item.action] || { icon: ActivityIcon, label: item.action };
  const tone = SEVERITY_TONE[item.severity] || SEVERITY_TONE.INFO;
  const Icon = meta.icon;
  const env = item.envelope || {};
  const geoStr = env.geo
    ? [env.geo.city, env.geo.region, env.geo.country].filter(Boolean).join(", ")
    : null;
  // Reuse the smart display resolver so deleted files, uploads, and
  // renames show meaningful text in the Owner view too (previously
  // deleted files displayed nothing since target lookup failed).
  const display = resolveFileDisplay(item);
  const showTargetChip = display.primary && display.primary !== "—";

  return (
    <div className="border-b border-border/60 last:border-b-0 hover:bg-secondary/40 transition" data-testid={`event-row-${item.id}`}>
      <button onClick={onToggle} className="w-full text-left px-4 py-3 flex items-start gap-3">
        <span className={`mt-0.5 w-8 h-8 grid place-items-center rounded-md ${tone.soft}`}>
          <Icon className={`w-4 h-4 ${tone.text}`} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className="font-medium">
              {(item.user && (item.user.name || item.user.email)) || "System"}
            </span>
            <span className="text-muted-foreground">{meta.label.toLowerCase()}</span>
            {showTargetChip && (
              <span
                className={`mono text-xs px-1.5 py-0.5 rounded bg-secondary text-foreground/80 truncate max-w-[300px] ${display.strike ? "line-through" : ""}`}
                title={display.primary}
              >
                {display.primary}
              </span>
            )}
            {display.secondary && (
              <span className="label-mono text-muted-foreground truncate max-w-[220px]" title={display.secondary}>
                · {display.secondary}
              </span>
            )}
            {item.room?.name && (
              <Link
                to={`/app/rooms/${item.room.id}`}
                onClick={(e) => e.stopPropagation()}
                className="mono text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary hover:bg-primary/20 transition"
              >
                {item.room.name}
              </Link>
            )}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2 label-mono">
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border ${tone.chip}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${tone.dot}`} />
              {item.severity}
            </span>
            <span className="text-muted-foreground">·</span>
            <span title={fmtAbsolute(item.ts)}>{fmtRelative(item.ts)}</span>
            {env.ip && (
              <>
                <span className="text-muted-foreground">·</span>
                <span className="mono text-[10px]" title={env.user_agent || ""}>{env.ip}</span>
              </>
            )}
            {geoStr && (
              <>
                <span className="text-muted-foreground">·</span>
                <span className="text-muted-foreground inline-flex items-center gap-0.5">
                  <Globe className="w-2.5 h-2.5" />{geoStr}
                </span>
              </>
            )}
            {env.session_id && (
              <>
                <span className="text-muted-foreground">·</span>
                <button
                  onClick={(e) => { e.stopPropagation(); onSession(env.session_id); }}
                  data-testid={`event-session-${item.id}`}
                  className="mono text-[10px] text-primary hover:underline"
                  title="Open session replay"
                >
                  session {env.session_id.slice(0, 8)}
                </button>
              </>
            )}
          </div>
        </div>
        <span className="text-muted-foreground pt-1">
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </span>
      </button>
      {expanded && <ExpandedDetails item={item} />}
    </div>
  );
}

function ExpandedDetails({ item }) {
  const env = item.envelope || {};
  const chain = item.chain || {};
  const rows = [
    ["Full timestamp", fmtAbsolute(item.ts)],
    ["Category", item.category],
    ["Action key", item.action],
    ["IP", env.ip || "—"],
    ["Device", env.device || "—"],
    ["Browser", env.browser || "—"],
    ["OS", env.os || "—"],
    ["User-Agent", env.user_agent || "—"],
    ["Geo", env.geo ? `${env.geo.city ?? "?"} · ${env.geo.region ?? "?"} · ${env.geo.country ?? "?"} (${env.geo.country_code ?? "?"})` : "—"],
    ["Session id", env.session_id || "—"],
    ["Request id", env.request_id || "—"],
    ["Hash", chain.hash || "— (legacy row, pre-chain)"],
    ["Prev hash", chain.prev_hash || "—"],
  ];
  return (
    <div className="px-4 pb-4 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1 text-xs">
      {rows.map(([k, v]) => (
        <div key={k} className="flex gap-3 py-1 border-b border-border/40 last:border-b-0">
          <span className="label-mono text-muted-foreground w-32 flex-shrink-0">{k}</span>
          <span className="mono break-all">{v}</span>
        </div>
      ))}
      {Object.keys(item.meta || {}).length > 0 && (
        <div className="md:col-span-2 mt-3">
          <div className="label-mono text-muted-foreground mb-1">Meta payload</div>
          <pre className="mono text-[11px] p-3 rounded-md bg-secondary/40 border border-border overflow-x-auto">
{JSON.stringify(item.meta, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

function SessionDrawer({ sessionId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get(`/audit/sessions/${sessionId}`)
      .then((r) => { if (!cancel) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [sessionId]);

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="session-drawer">
      <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-[560px] bg-background border-l border-border overflow-y-auto">
        <div className="sticky top-0 bg-background/95 backdrop-blur border-b border-border px-5 py-4 flex items-center justify-between">
          <div>
            <div className="label-mono text-muted-foreground">Session replay</div>
            <div className="mono text-xs mt-1 break-all">{sessionId}</div>
          </div>
          <button onClick={onClose} data-testid="session-drawer-close" className="p-2 hover:bg-secondary rounded-md">
            <X className="w-4 h-4" />
          </button>
        </div>
        {loading && <div className="p-6 text-sm text-muted-foreground">Loading…</div>}
        {data && (
          <>
            <div className="p-5 border-b border-border">
              <div className="text-lg font-semibold">{data.user?.name || data.user?.email}</div>
              <div className="mt-1 text-xs text-muted-foreground">
                {fmtAbsolute(data.started_at)} → {fmtAbsolute(data.ended_at)}
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 label-mono">
                <div><span className="text-muted-foreground">Events</span><br/>{data.events}</div>
                <div><span className="text-muted-foreground">IP</span><br/><span className="mono">{data.ip || "—"}</span></div>
                <div><span className="text-muted-foreground">Device</span><br/>{data.device || "—"}</div>
                <div className="col-span-3"><span className="text-muted-foreground">Location</span><br/>
                  {data.geo ? [data.geo.city, data.geo.region, data.geo.country].filter(Boolean).join(", ") : "—"}
                </div>
              </div>
            </div>
            <div className="divide-y divide-border/60">
              {data.items.map((it) => {
                const meta = ACTION_META[it.action] || { icon: ActivityIcon, label: it.action };
                const tone = SEVERITY_TONE[it.severity] || SEVERITY_TONE.INFO;
                const Icon = meta.icon;
                return (
                  <div key={it.id} className="px-5 py-3 flex items-start gap-3 text-sm">
                    <Icon className={`w-4 h-4 mt-0.5 ${tone.text}`} />
                    <div className="flex-1 min-w-0">
                      <div>{meta.label}
                        {it.target?.name && <span className="ml-2 mono text-xs text-muted-foreground truncate">{it.target.name}</span>}
                      </div>
                      <div className="mt-0.5 label-mono">{fmtAbsolute(it.ts)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function groupByDay(items) {
  const out = [];
  let lastKey = null;
  for (const it of items) {
    const d = new Date(it.ts);
    const key = d.toDateString();
    if (key !== lastKey) {
      out.push({ kind: "header", key, label: humanDateHeader(d) });
      lastKey = key;
    }
    out.push({ kind: "row", item: it });
  }
  return out;
}

// Resolve the "name to show in the File column" for a file/folder
// event. The target may be gone (deleted files) or the interesting
// piece of info may live in `meta` (rename before/after, upload
// name, delete name). Falls back through target.name → meta.to →
// meta.name → "—". Returns { primary, secondary } so renames can
// show "New name" with "was: Old name" underneath.
function resolveFileDisplay(it) {
  const action = it.action || "";
  const target = it.target || {};
  const meta = it.meta || {};
  // Rename: prefer meta.to as the current name, meta.from as the
  // "was" trailer. If either is missing, use whatever we have.
  if (action.endsWith("_renamed")) {
    const to = meta.to || target.name || null;
    const from = meta.from || null;
    return {
      primary: to || "—",
      secondary: from ? `was: ${from}` : null,
      strike: false,
    };
  }
  // Delete: target is gone from the collection, use meta.name.
  // Strike-through so the deletion reads at a glance.
  if (action.endsWith("_deleted")) {
    return {
      primary: meta.name || target.name || "—",
      secondary: null,
      strike: true,
    };
  }
  // Upload: target.name usually resolves too, but meta.name is
  // authoritative and stays consistent even if the doc is later
  // renamed. Include size if we captured it.
  if (action === "document_uploaded") {
    const sizeKb = meta.size ? `${Math.round(meta.size / 1024)} KB` : null;
    return {
      primary: meta.name || target.name || "—",
      secondary: sizeKb,
      strike: false,
    };
  }
  // Copy: target is the source doc; meta.new_id is the copy. Show
  // source name with a small "→ copy" hint so the audit line is
  // self-contained without cross-referencing.
  if (action.endsWith("_copied")) {
    return {
      primary: target.name || meta.name || "—",
      secondary: "copied to another location",
      strike: false,
    };
  }
  // Move: target is the moved doc; meta.from/to are parent folder ids.
  // Resolving them to folder names on the fly is expensive; a plain
  // "moved" descriptor is enough for the admin view.
  if (action.endsWith("_moved")) {
    return {
      primary: target.name || meta.name || "—",
      secondary: "moved to another folder",
      strike: false,
    };
  }
  // Everything else (views, access updates, folder_created…) — the
  // target lookup usually resolves; meta.name is the fallback.
  return {
    primary: target.name || meta.name || "—",
    secondary: null,
    strike: false,
  };
}

export default function ActivityPage() {
  const { user } = useAuth();
  // Route to the appropriate experience based on Owner vs. plain-admin.
  // The audit envelope + hash chain endpoints (/audit/*) are Owner-only
  // on the backend as well, so a spoofed DOM couldn't leak that data.
  if (user?.owner) return <OwnerAuditPage />;
  return <AdminFileHistoryPage />;
}

// ---- Admin (non-Owner) — simplified file history table --------------

function AdminFileHistoryPage() {
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [opts, setOpts] = useState({ rooms: [], users: [] });
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ q: "", days: 30, room_id: "", user_id: "" });

  const buildParams = useCallback((extra = {}) => {
    const p = { limit: 200, days: f.days };
    if (f.q.trim()) p.q = f.q.trim();
    if (f.room_id) p.room_id = f.room_id;
    if (f.user_id) p.user_id = f.user_id;
    return { ...p, ...extra };
  }, [f]);

  const fetchFirst = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/audit/file-history", { params: buildParams() });
      setItems(data.items);
      setNextCursor(data.next_cursor);
    } finally { setLoading(false); }
  }, [buildParams]);

  const fetchMore = async () => {
    if (!nextCursor) return;
    const { data } = await api.get("/audit/file-history", {
      params: buildParams({ cursor: nextCursor }),
    });
    setItems((cur) => [...cur, ...data.items]);
    setNextCursor(data.next_cursor);
  };

  // Options endpoint requires Owner. Admins can still populate dropdowns
  // via lightweight fallbacks — hit /api/rooms + /api/users (both
  // admin-accessible) instead.
  useEffect(() => {
    Promise.all([
      api.get("/rooms").catch(() => ({ data: [] })),
      api.get("/users").catch(() => ({ data: [] })),
    ]).then(([r, u]) => setOpts({
      rooms: (r.data || []).map((x) => ({ id: x.id, name: x.name })),
      users: (u.data || []).map((x) => ({ id: x.id, name: x.name || x.email, email: x.email })),
    }));
  }, []);

  useEffect(() => { fetchFirst(); }, [fetchFirst]);

  const activeFilterCount =
    (f.q ? 1 : 0) + (f.room_id ? 1 : 0) + (f.user_id ? 1 : 0);

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-6 md:p-8 space-y-6 max-w-[1400px] mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="label-mono text-muted-foreground inline-flex items-center gap-2">
              <ActivityIcon className="w-3.5 h-3.5" /> — File activity
            </div>
            <h1 className="font-display text-4xl tracking-tighter font-medium mt-1">Who did what</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              Every file &amp; folder action across your rooms — uploads,
              downloads, views, renames, moves, deletes. For sign-in
              forensics, IP / geo tracking and the tamper-evident hash
              chain, ask an App Owner.
            </p>
          </div>
          <button
            onClick={fetchFirst}
            data-testid="admin-file-refresh"
            className="inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded-md border border-border hover:bg-secondary transition"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>

        <div className="border border-border rounded-lg bg-card p-3 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
              <input
                data-testid="admin-file-search"
                value={f.q}
                onChange={(e) => setF((c) => ({ ...c, q: e.target.value }))}
                placeholder="Search file name or action…"
                className="w-full pl-8 pr-3 py-1.5 text-sm bg-background border border-border rounded-md outline-none focus:border-primary/50 transition"
              />
            </div>
            {DATE_SHORTCUTS.map((d) => (
              <button
                key={d.key}
                data-testid={`admin-file-days-${d.key}`}
                onClick={() => setF((c) => ({ ...c, days: d.key }))}
                className={`px-2.5 py-1 text-xs mono uppercase rounded-md border transition ${
                  f.days === d.key
                    ? "border-primary/40 bg-primary/10 text-primary"
                    : "border-border hover:bg-secondary"
                }`}
              >
                {d.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              data-testid="admin-file-user"
              value={f.user_id}
              onChange={(e) => setF((c) => ({ ...c, user_id: e.target.value }))}
              className="text-xs bg-background border border-border rounded-md px-2 py-1"
            >
              <option value="">All users</option>
              {opts.users.map((u) => (
                <option key={u.id} value={u.id}>{u.name || u.email}</option>
              ))}
            </select>
            <select
              data-testid="admin-file-room"
              value={f.room_id}
              onChange={(e) => setF((c) => ({ ...c, room_id: e.target.value }))}
              className="text-xs bg-background border border-border rounded-md px-2 py-1"
            >
              <option value="">All rooms</option>
              {opts.rooms.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
            {activeFilterCount > 0 && (
              <button
                onClick={() => setF({ q: "", days: 30, room_id: "", user_id: "" })}
                className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
              >
                <X className="w-3 h-3" /> Clear {activeFilterCount} filter{activeFilterCount === 1 ? "" : "s"}
              </button>
            )}
          </div>
        </div>

        <div className="border border-border rounded-lg bg-card overflow-hidden" data-testid="admin-file-table">
          {loading && !items.length && (
            <div className="p-8 text-sm text-muted-foreground text-center">Loading…</div>
          )}
          {!loading && !items.length && (
            <div className="p-10 text-center">
              <ActivityIcon className="w-10 h-10 mx-auto text-muted-foreground opacity-50" />
              <div className="mt-3 text-sm">No file activity in this range.</div>
            </div>
          )}
          {!!items.length && (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-border/60 bg-secondary/30">
                  <th className="px-4 py-2 label-mono text-muted-foreground">File</th>
                  <th className="px-4 py-2 label-mono text-muted-foreground">Action</th>
                  <th className="px-4 py-2 label-mono text-muted-foreground">Actor</th>
                  <th className="px-4 py-2 label-mono text-muted-foreground">Room</th>
                  <th className="px-4 py-2 label-mono text-muted-foreground w-32">When</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const meta = ACTION_META[it.action] || { icon: ActivityIcon, label: it.action };
                  const Icon = meta.icon;
                  const display = resolveFileDisplay(it);
                  return (
                    <tr
                      key={it.id}
                      data-testid={`admin-file-row-${it.id}`}
                      className="border-b border-border/40 last:border-b-0 hover:bg-secondary/40 transition align-top"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-start gap-2 min-w-0">
                          <Icon className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${display.strike ? "text-rose-500" : "text-muted-foreground"}`} />
                          <div className="min-w-0 flex-1">
                            <div
                              className={`truncate ${display.strike ? "line-through text-muted-foreground" : ""}`}
                              title={display.primary}
                              data-testid={`admin-file-name-${it.id}`}
                            >
                              {display.primary}
                            </div>
                            {display.secondary && (
                              <div
                                className="label-mono text-muted-foreground mt-0.5 truncate"
                                title={display.secondary}
                                data-testid={`admin-file-secondary-${it.id}`}
                              >
                                {display.secondary}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{meta.label}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {(it.user && (it.user.name || it.user.email)) || "System"}
                      </td>
                      <td className="px-4 py-3">
                        {it.room?.name ? (
                          <Link
                            to={`/app/rooms/${it.room.id}`}
                            className="mono text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary hover:bg-primary/20 transition whitespace-nowrap"
                          >
                            {it.room.name}
                          </Link>
                        ) : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-4 py-3 label-mono whitespace-nowrap" title={fmtAbsolute(it.ts)}>
                        {fmtRelative(it.ts)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {nextCursor && (
            <div className="p-4 text-center">
              <button
                onClick={fetchMore}
                data-testid="admin-file-load-more"
                className="text-xs text-primary hover:underline"
              >
                Load older events
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- Owner — boardroom-grade audit console --------------------------

function OwnerAuditPage() {
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [summary, setSummary] = useState(null);
  const [chainState, setChainState] = useState({ checked: 0, intact: true });
  const [opts, setOpts] = useState({ rooms: [], users: [], actions: [], categories: [], severities: [] });
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [drawerSession, setDrawerSession] = useState(null);
  const [liveTail, setLiveTail] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const tailRef = useRef(null);

  const [f, setF] = useState({
    q: "",
    days: 30,
    severity: [],
    category: [],
    room_id: "",
    user_id: "",
  });

  const buildParams = useCallback((extra = {}) => {
    const p = { limit: 100, days: f.days };
    if (f.q.trim()) p.q = f.q.trim();
    if (f.severity.length) p.severity = f.severity.join(",");
    if (f.category.length) p.category = f.category.join(",");
    if (f.room_id) p.room_id = f.room_id;
    if (f.user_id) p.user_id = f.user_id;
    return { ...p, ...extra };
  }, [f]);

  const fetchFirst = useCallback(async () => {
    setLoading(true);
    try {
      const [feed, sum, chain] = await Promise.all([
        api.get("/audit/activity", { params: buildParams() }),
        api.get("/audit/summary", { params: { days: f.days } }),
        api.get("/audit/chain/verify", { params: { days: f.days } }),
      ]);
      setItems(feed.data.items);
      setNextCursor(feed.data.next_cursor);
      setSummary(sum.data);
      setChainState(chain.data);
    } finally {
      setLoading(false);
    }
  }, [buildParams, f.days]);

  const fetchMore = async () => {
    if (!nextCursor) return;
    const { data } = await api.get("/audit/activity", { params: buildParams({ cursor: nextCursor }) });
    setItems((cur) => [...cur, ...data.items]);
    setNextCursor(data.next_cursor);
  };

  const pollTail = useCallback(async () => {
    const { data } = await api.get("/audit/activity", { params: buildParams() });
    setItems((cur) => {
      const known = new Set(cur.map((x) => x.id));
      const fresh = data.items.filter((x) => !known.has(x.id));
      if (!fresh.length) return cur;
      return [...fresh, ...cur];
    });
  }, [buildParams]);

  useEffect(() => {
    api.get("/audit/options").then(({ data }) => setOpts(data)).catch(() => {});
  }, []);

  useEffect(() => { fetchFirst(); }, [fetchFirst]);

  useEffect(() => {
    if (!liveTail) {
      if (tailRef.current) clearInterval(tailRef.current);
      tailRef.current = null;
      return undefined;
    }
    tailRef.current = setInterval(pollTail, 5000);
    return () => { if (tailRef.current) clearInterval(tailRef.current); };
  }, [liveTail, pollTail]);

  const exportFile = async (kind) => {
    setDownloading(kind);
    // PDF export can take 10–15s on large ranges because WeasyPrint
    // renders every row. Toast up-front so the user knows the click
    // was received and something is happening.
    const toastId = toast.loading(
      kind === "pdf"
        ? "Rendering PDF — this can take 10–15s for large ranges…"
        : "Preparing CSV…"
    );
    try {
      const res = await api.get(`/audit/export.${kind}`, {
        params: buildParams({ limit: undefined }),
        responseType: "blob",
        timeout: 120000, // WeasyPrint on 5000 rows can push 30s.
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = res.headers["content-disposition"]?.match(/filename="([^"]+)"/)?.[1]
        || `audit.${kind}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${a.download}`, { id: toastId });
    } catch (e) {
      toast.error(`Export failed`, { id: toastId });
    } finally {
      setDownloading(null);
    }
  };

  const groupedRows = useMemo(() => groupByDay(items), [items]);

  const toggleSet = (key, val) => setF((cur) => {
    const arr = cur[key];
    return { ...cur, [key]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] };
  });

  const activeFilterCount =
    (f.q ? 1 : 0) + f.severity.length + f.category.length + (f.room_id ? 1 : 0) + (f.user_id ? 1 : 0);

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-6 md:p-8 space-y-6 max-w-[1400px] mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="label-mono text-muted-foreground inline-flex items-center gap-2">
              <ActivityIcon className="w-3.5 h-3.5" /> — Audit trail
            </div>
            <h1 className="font-display text-4xl tracking-tighter font-medium mt-1">Activity history</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              Every event across your data rooms — with IP, device, geo,
              session-id and a tamper-evident SHA-256 hash chain. Filter,
              search, replay a session, or export for auditors.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ChainPill
              checked={chainState.checked}
              intact={chainState.intact}
              onOpen={fetchFirst}
            />
            <button
              onClick={() => setLiveTail((v) => !v)}
              data-testid="live-tail-toggle"
              className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded-md border transition ${
                liveTail
                  ? "border-primary/40 bg-primary/10 text-primary"
                  : "border-border hover:bg-secondary"
              }`}
              title="Poll every 5 seconds and prepend new events"
            >
              {liveTail ? <Radio className="w-3.5 h-3.5 animate-pulse" /> : <Play className="w-3.5 h-3.5" />}
              {liveTail ? "Live tail on" : "Live tail"}
            </button>
            <button
              onClick={fetchFirst}
              data-testid="audit-refresh"
              className="inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded-md border border-border hover:bg-secondary transition"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
        </div>

        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="audit-kpis">
            <KpiCard label="Events"      value={summary.cards.total.current}       delta={summary.cards.total.delta}       Icon={ActivityIcon}  tone="text-primary bg-primary/10" />
            <KpiCard label="Sign-ins"    value={summary.cards.sign_ins.current}    delta={summary.cards.sign_ins.delta}    Icon={LogIn}         tone="text-amber-500 bg-amber-500/10" />
            <KpiCard label="Downloads"   value={summary.cards.downloads.current}   delta={summary.cards.downloads.delta}   Icon={FileDown}      tone="text-sky-500 bg-sky-500/10" />
            <KpiCard label="Uploads"     value={summary.cards.uploads.current}     delta={summary.cards.uploads.delta}     Icon={Upload}        tone="text-emerald-500 bg-emerald-500/10" />
            <KpiCard label="Destructive" value={summary.cards.destructive.current} delta={summary.cards.destructive.delta} Icon={Trash2}        tone="text-rose-500 bg-rose-500/10" />
          </div>
        )}

        <div className="border border-border rounded-lg bg-card p-3 space-y-3 sticky top-0 z-10 backdrop-blur">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
              <input
                data-testid="audit-search"
                value={f.q}
                onChange={(e) => setF((c) => ({ ...c, q: e.target.value }))}
                placeholder="Search action / IP / user-agent / target…"
                className="w-full pl-8 pr-3 py-1.5 text-sm bg-background border border-border rounded-md outline-none focus:border-primary/50 transition"
              />
            </div>
            {DATE_SHORTCUTS.map((d) => (
              <button
                key={d.key}
                data-testid={`audit-days-${d.key}`}
                onClick={() => setF((c) => ({ ...c, days: d.key }))}
                className={`px-2.5 py-1 text-xs mono uppercase rounded-md border transition ${
                  f.days === d.key
                    ? "border-primary/40 bg-primary/10 text-primary"
                    : "border-border hover:bg-secondary"
                }`}
              >
                {d.label}
              </button>
            ))}
            <div className="w-px h-6 bg-border" />
            <button
              onClick={() => exportFile("csv")}
              disabled={downloading === "csv"}
              data-testid="audit-export-csv"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs mono uppercase rounded-md border border-border hover:bg-secondary transition disabled:opacity-50"
            >
              {downloading === "csv"
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <Download className="w-3.5 h-3.5" />}
              CSV
            </button>
            <button
              onClick={() => exportFile("pdf")}
              disabled={downloading === "pdf"}
              data-testid="audit-export-pdf"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs mono uppercase rounded-md border border-border hover:bg-secondary transition disabled:opacity-50"
            >
              {downloading === "pdf"
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <FileText className="w-3.5 h-3.5" />}
              PDF
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-2 label-mono">
            <span className="text-muted-foreground">Severity:</span>
            {Object.keys(SEVERITY_TONE).map((sev) => {
              const active = f.severity.includes(sev);
              const tone = SEVERITY_TONE[sev];
              return (
                <button
                  key={sev}
                  data-testid={`audit-sev-${sev}`}
                  onClick={() => toggleSet("severity", sev)}
                  className={`px-2 py-0.5 rounded-full border transition ${active ? tone.chip : "border-border hover:bg-secondary text-muted-foreground"}`}
                >
                  {sev}
                </button>
              );
            })}
            <span className="mx-1 w-px h-4 bg-border" />
            <span className="text-muted-foreground">Category:</span>
            {CATEGORIES.map((c) => {
              const active = f.category.includes(c.key);
              return (
                <button
                  key={c.key}
                  data-testid={`audit-cat-${c.key}`}
                  onClick={() => toggleSet("category", c.key)}
                  className={`px-2 py-0.5 rounded-full border transition ${
                    active ? "border-primary/40 bg-primary/10 text-primary" : "border-border hover:bg-secondary text-muted-foreground"
                  }`}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              data-testid="audit-user"
              value={f.user_id}
              onChange={(e) => setF((c) => ({ ...c, user_id: e.target.value }))}
              className="text-xs bg-background border border-border rounded-md px-2 py-1"
            >
              <option value="">All users</option>
              {opts.users.map((u) => (
                <option key={u.id} value={u.id}>{u.name || u.email}</option>
              ))}
            </select>
            <select
              data-testid="audit-room"
              value={f.room_id}
              onChange={(e) => setF((c) => ({ ...c, room_id: e.target.value }))}
              className="text-xs bg-background border border-border rounded-md px-2 py-1"
            >
              <option value="">All rooms</option>
              {opts.rooms.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
            {activeFilterCount > 0 && (
              <button
                data-testid="audit-clear"
                onClick={() => setF({ q: "", days: 30, severity: [], category: [], room_id: "", user_id: "" })}
                className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
              >
                <X className="w-3 h-3" /> Clear {activeFilterCount} filter{activeFilterCount === 1 ? "" : "s"}
              </button>
            )}
          </div>
        </div>

        <div className="border border-border rounded-lg bg-card overflow-hidden" data-testid="audit-timeline">
          {loading && !items.length && (
            <div className="p-8 text-sm text-muted-foreground text-center">Loading events…</div>
          )}
          {!loading && !items.length && (
            <div className="p-10 text-center">
              <ActivityIcon className="w-10 h-10 mx-auto text-muted-foreground opacity-50" />
              <div className="mt-3 text-sm">No events match your filters.</div>
              <div className="mt-1 label-mono text-muted-foreground">Try widening the date range or clearing filters.</div>
            </div>
          )}
          {groupedRows.map((g) => g.kind === "header" ? (
            <div key={g.key} className="px-4 py-2 label-mono text-muted-foreground bg-secondary/30 border-y border-border/60">
              {g.label}
            </div>
          ) : (
            <EventRow
              key={g.item.id}
              item={g.item}
              expanded={expandedId === g.item.id}
              onToggle={() => setExpandedId((c) => c === g.item.id ? null : g.item.id)}
              onSession={setDrawerSession}
            />
          ))}
          {nextCursor && (
            <div className="p-4 text-center">
              <button
                onClick={fetchMore}
                data-testid="audit-load-more"
                className="text-xs text-primary hover:underline"
              >
                Load older events
              </button>
            </div>
          )}
        </div>
      </div>

      {drawerSession && (
        <SessionDrawer sessionId={drawerSession} onClose={() => setDrawerSession(null)} />
      )}
    </div>
  );
}
